import { removeAllStorage } from 'app/utils/utils';
import axios from 'axios';

/**
 * Contrato de expulsion con i2c (mitad de frontend) para la sesion unica Mint.
 * Cuando la blacklist de JWT revoca el token de una sesion backoffice cerrada,
 * i2c NO responde por SERVICE_RESPONSES.responseError sino por el handler de
 * seguridad de swagger: ensureAuth serializa un error cuyo status HTTP es 403 y
 * cuyo cuerpo lleva errorKey: 'SESSION_REVOKED' a NIVEL RAIZ
 * (response.data.errorKey, NO response.data.error.errorKey). Ese 403 lo captura
 * el manejador generico de 403 de abajo, que ya limpia la sesion y redirige a
 * /auth/login: la revocacion viaja como un 403 mas y no necesita logica extra.
 *
 * El 409 ACTIVE_SESSION_EXISTS del login es un camino distinto: sale por
 * SERVICE_RESPONSES.responseError con forma { error: { errorKey } } y lo maneja
 * la LoginPage, no este interceptor.
 */

/**
 * Configura interceptores globales de axios para la aplicacion
 *
 * Request Interceptor:
 * - Inyecta token de autenticacion en headers Authorization
 * - Configura baseURL desde configuracion de entorno
 *
 * Response Interceptor:
 * - Maneja errores 403 (denegacion de rol o revocacion de sesion Mint via
 *   errorKey SESSION_REVOKED) redirigiendo a login
 * - Distingue entre requests canceladas y errores reales
 * - Retorna estructura consistente { error } para manejo de errores
 * - Retorna { error: null, cancelled: true } para cancelaciones
 *
 * @param {Object} axiosInstance - Instancia de axios
 * @param {string} baseUrl - URL base de la API
 * @param {Object} store - Redux store para acceder a authToken
 */
export function setupAxios(axiosInstance, baseUrl, store) {
  axiosInstance.interceptors.request.use(
    (config) => {
      const {
        auth: { authToken },
      } = store.getState();

      if (authToken) {
        config.headers.Authorization = `${authToken}`;
      }

      config.baseURL = baseUrl;
      return config;
    },
    (err) => Promise.reject(err)
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Un 403 (denegacion de rol o revocacion de sesion Mint via SESSION_REVOKED,
      // ver cabecera) limpia la sesion local y vuelve al login.
      if (error.response?.status === 403) {
        removeAllStorage();
        window.location.href = '/auth/login';
        return;
      }

      if (axios.isCancel(error)) {
        return {
          error: null,
          cancelled: true,
          message: error.message || 'Request cancelled',
        };
      }

      return {
        error: error.response || error,
      };
    }
  );
}
